// Sales Chart Component
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency } from '../utils/calculations';

interface SalesChartProps {
  data: Array<{
    date: string;
    revenue: number;
    revenueHT: number;
    invoiceCount: number;
  }>;
  type?: 'line' | 'bar';
}

export function SalesTrendChart({ data, type = 'line' }: SalesChartProps) {
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">
            {new Date(payload[0].payload.date).toLocaleDateString('fr-DZ', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              CA TTC: {formatCurrency(payload[0].payload.revenue)}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              CA HT: {formatCurrency(payload[0].payload.revenueHT)}
            </p>
            <p className="text-sm text-muted-foreground">
              Factures: {payload[0].payload.invoiceCount}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const ChartComponent = type === 'line' ? LineChart : BarChart;
  const DataComponent = type === 'line' ? Line : Bar;

  return (
    <div className="w-full h-80 bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        <span className="font-arabic">منحنى المبيعات</span>
        <span className="text-muted-foreground mx-2">/</span>
        <span>Tendance des ventes</span>
      </h3>
      
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="date"
            tickFormatter={(date) => new Date(date).toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit' })}
            className="text-muted-foreground"
          />
          <YAxis
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            className="text-muted-foreground"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {type === 'line' ? (
            <>
              <Line
                type="monotone"
                dataKey="revenue"
                name="CA TTC"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="revenueHT"
                name="CA HT"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </>
          ) : (
            <>
              <Bar dataKey="revenue" name="CA TTC" fill="#10b981" />
              <Bar dataKey="revenueHT" name="CA HT" fill="#3b82f6" />
            </>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}

interface TopProductsChartProps {
  data: Array<{
    productName: string;
    revenue: number;
  }>;
}

export function TopProductsChart({ data }: TopProductsChartProps) {
  const top10 = data.slice(0, 10);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-1">
            {payload[0].payload.productName}
          </p>
          <p className="text-sm text-emerald-600 dark:text-emerald-400">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-96 bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        <span className="font-arabic">أفضل 10 منتجات</span>
        <span className="text-muted-foreground mx-2">/</span>
        <span>Top 10 Produits</span>
      </h3>
      
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={top10} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            type="number"
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
            className="text-muted-foreground"
          />
          <YAxis
            type="category"
            dataKey="productName"
            width={150}
            className="text-muted-foreground text-sm"
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="revenue" fill="#10b981" radius={[0, 8, 8, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
